import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext } from 'next/types';

import { homecleanState } from 'atoms';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import Footer from '@components/common/Footer';
import Header from '@components/common/Header';
import DynamicPage from '@components/parts/DynamicPage';

import { fetchAggregate } from '@utils/api';
import { PAGE_PATH } from '@utils/constants';
import { determineTemplateId } from '@utils/determine-template-id';
import { validateRfqServiceCode } from '@utils/get-service-code';

import { DynamicData } from '@customTypes/DynamicData';
import { GlobalData } from '@customTypes/GlobalData';

type ServicePageProps = {
  code: string;
  global: GlobalData;
  data: any;
  dynamicData: DynamicData;
};

function ServicePage(props: ServicePageProps) {
  const router = useRouter();
  const pageViewer = `${router.query?.pageViewer || 'web'}`;
  const { code, global, data, dynamicData } = props;
  const { isHomecleanIframeExpanded } = useRecoilValue(homecleanState);

  return (
    <PageContainer isOverflowY={isHomecleanIframeExpanded}>
      <Head>
        <title>{data?.siteDescription || dynamicData?.siteDescription || global?.siteDescription || ''}</title>
        <link rel="icon" href={global?.favicon?.data?.attributes?.url || ''} />
        <link rel="canonical" href={`https://miso.kr/${code}`} />
        {code.includes('moving') ? (
          <>
            <meta
              content={'https://miso-customer-public.s3.ap-northeast-2.amazonaws.com/img/miso-mv-thumbnail.png'}
              property="og:image"
            />
            <meta
              content={'https://miso-customer-public.s3.ap-northeast-2.amazonaws.com/img/miso-mv-thumbnail.png'}
              property="twitter:image"
            />
          </>
        ) : null}
      </Head>
      <Header data={global} pageViewer={pageViewer} />
      <DynamicPage slug={code} global={global} data={data} dynamicData={dynamicData} pageViewer={pageViewer} />
      <Footer data={global} pageViewer={pageViewer} />
    </PageContainer>
  );
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<
  { props: ServicePageProps } | { notFound: boolean } | { redirect: { destination: string; permanent: false } }
> {
  const { res } = context;
  const { slug } = context.params || {};

  const isInternet = slug === PAGE_PATH.INTERNET;

  // NOTE:
  // '/internet' 경로가 상위에 노출되고 있지만, 실제로 사용되고 있는 페이지는
  // next-face를 통해 배포된 '/booking/forms/internet' 경로입니다.
  if (isInternet) {
    return {
      redirect: {
        destination: '/booking/forms/internet',
        permanent: false, // 임시 리디렉션 (HTTP 307)
      },
    };
  }

  // NOTE: internet deal을 api => zapier 로 통신하기 위한 code 241028일 제거 예정
  const newSlug = slug === PAGE_PATH.INTERNET_ZAPIER ? PAGE_PATH.INTERNET : slug;

  if (!newSlug || Array.isArray(newSlug)) {
    throw Error('No given slug');
  }

  const isValidSlug = validateRfqServiceCode(newSlug as string);
  if (!isValidSlug) {
    res.statusCode = 404;
    return { notFound: true };
  }
  const [templateId, serviceId] = determineTemplateId(newSlug);

  const props = await fetchAggregate(templateId, serviceId);
  res.setHeader('Cache-Control', 'public, max-age=6048000, immutable');
  return { props };
}

export default ServicePage;

const PageContainer = styled.div<{ isOverflowY?: boolean }>`
  overflow-y: ${(props) => (props.isOverflowY ? 'hidden !important' : 'auto')};
  height: ${(props) => (props.isOverflowY ? '100vh' : 'auto')};
`;
